import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { Typography } from '@design-system/components/typography/typography';
import { Checklist } from '../model';
import { ChecklistProgress } from './ChecklistProgress';
import { ChecklistTaskItem } from './ChecklistTaskItem';
import * as styles from './GoalSection.css';
import clsx from 'clsx';

export type GoalType = 'previous' | 'current' | 'next';

type GoalSectionProps = {
  checklist: Checklist;
  checklistNumber: number;
  goalType: GoalType;
  isOpening: boolean;
  isCollapsed: boolean;
  isCompleted: boolean;
  influencerId: number;
  onOpen: (checklistId: string) => void;
  onCollapse: () => void;
  onTaskToggle: (taskId: string, completed: boolean) => void;
  onOpenTaskModal: (templateId: string) => void;
};

const getGoalLabel = (checklistNumber: number): string => {
  return `Checklist #${checklistNumber}`;
};

const getLabelStyle = (goalType: GoalType) =>
  goalType === 'current' ? styles.goalLabelCurrent : styles.goalLabelInactive;

export const GoalSection = ({
  checklist,
  checklistNumber,
  goalType,
  isOpening,
  isCollapsed,
  isCompleted,
  influencerId,
  onOpen,
  onCollapse,
  onTaskToggle,
  onOpenTaskModal,
}: GoalSectionProps) => {
  const completedTasks = checklist.tasks.filter((t) => t.isChecked).length;
  const totalTasks = checklist.tasks.length;
  const hasProgress = completedTasks > 0;
  const isExpanded = goalType === 'current' && !isCollapsed;

  const handleHeaderClick = () => {
    if (goalType === 'current') {
      onCollapse();
    } else {
      onOpen(checklist.id);
    }
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpen(checklist.id);
  };

  const isCurrent = goalType === 'current';
  const isInactive = !isCurrent;
  const showProgressBar = !isCompleted && (isCurrent || hasProgress);

  return (
    <div
      className={clsx(
        styles.goalSection,
        isCompleted && styles.completedSection,
      )}
    >
      <div className={styles.goalHeader} onClick={handleHeaderClick}>
        <div className={styles.goalHeaderContent}>
          <div className={getLabelStyle(goalType)}>
            {getGoalLabel(checklistNumber)}
          </div>
          <Typography
            variant={isExpanded ? 'title' : 'body'}
            size={isExpanded ? undefined : 'large'}
            fontWeight="bold"
            className={styles.goalTitle}
          >
            {checklist.title}
          </Typography>
        </div>
        {checklist.estimatedDurationLabel && (
          <span className={styles.estimatedTime}>
            <FontAwesomeIcon icon={faClock} className={styles.clockIcon} />
            {checklist.estimatedDurationLabel}
          </span>
        )}
        <div className={styles.headerActions}>
          {isCompleted && (
            <div className={styles.completedLabelContainer}>
              <FontAwesomeIcon
                icon={faCircleCheck}
                className={styles.completedCheckIcon}
              />
              <span className={styles.completedLabel}>Completed</span>
            </div>
          )}
          {!isCompleted && isInactive && (
            <button
              className={styles.actionLink}
              onClick={handleActionClick}
              disabled={isOpening}
            >
              {isOpening ? 'Opening...' : 'Explore'}
            </button>
          )}
        </div>
      </div>

      {showProgressBar && (
        <div className={styles.progressSection}>
          <ChecklistProgress completed={completedTasks} total={totalTasks} />
        </div>
      )}

      <div
        className={clsx(
          styles.tasksAnimationWrapper,
          isExpanded && styles.tasksAnimationWrapperExpanded,
        )}
      >
        <div className={styles.tasksContainer}>
          {checklist.tasks.map((task) => (
            <div key={task.id} className={styles.taskWrapper}>
              <ChecklistTaskItem
                task={task}
                onToggle={onTaskToggle}
                onOpenModal={onOpenTaskModal}
                influencerId={influencerId}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
